import React from 'react';
import {Link as ReactRouterLink} from 'react-router-dom';
import {IntegrationDetailsHeader} from './IntegrationDetailsHeader';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Image,
  Link,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';
import {IntegrationSection} from './components';
import {
  useGetAdAccounts,
  useQueueImportLinkedInAdAccounts,
} from '../../hooks/api/linkedin';
import Select from 'react-select';
import {useIntegrationFromContext} from './IntegrationDetailsContext';
import {
  useIntegrations,
  useUpdateIntegration,
} from '../../hooks/api/integrations';
import {DeleteIntegrationButton} from './DeleteIntegrationButton';
import {useTask} from '../../hooks/api/tasks';
import {useQueryClient} from '@tanstack/react-query';
import {IntegrationKeys} from '../../hooks/api/integrations';

export const LinkedInIntegrationDetails = () => {
  const integration = useIntegrationFromContext('linkedin');
  const integrations = useIntegrations({});
  const updateIntegrationMutation = useUpdateIntegration();
  const initialImportModal = useDisclosure();
  const queryClient = useQueryClient();

  const adAccounts = useGetAdAccounts({});
  const adAccountOptions = (adAccounts.data ?? []).map((adAccount) => ({
    label: adAccount.name,
    value: adAccount.id,
  }));

  const importTask = useTask(
    {type: 'importLinkedInAdAccounts'},
    {
      onTaskComplete: async () => {
        // Refetch the LinkedIn integration to get the updated lastImport date
        await queryClient.invalidateQueries({
          queryKey: IntegrationKeys.get('linkedin'),
        });
      },
    }
  );

  const queueLinkedInImport = useQueueImportLinkedInAdAccounts();
  const startInitialSync = () => {
    queueLinkedInImport.mutate(
      {},
      {
        onSuccess: () => {
          initialImportModal.onOpen();
        },
      }
    );
  };

  return (
    <Flex direction="column" gap="8">
      <IntegrationDetailsHeader>
        <DeleteIntegrationButton warningMessage="This will disable intent tracking and remove any connected ad accounts." />
      </IntegrationDetailsHeader>

      <InitialImportModal
        isOpen={initialImportModal.isOpen}
        onClose={initialImportModal.onClose}
      />

      {!integration.adClicksConfig?.lastImport && (
        <IntegrationSection>
          <Flex direction="column" gap="4">
            <Box fontSize="lg" fontWeight="500">
              Get Started
            </Box>
            <List spacing="2">
              <ListItem>
                <Flex alignItems="center" gap="2">
                  <Checkbox
                    colorScheme="green"
                    isChecked={!!integration.adAccountId}
                    isReadOnly={true}
                    size="lg"
                  />
                  Select an ad account to use
                </Flex>
              </ListItem>
              <ListItem>
                <Flex alignItems="center" gap="2">
                  <Checkbox
                    colorScheme="green"
                    isChecked={
                      !!integrations.data?.find(({type}) => type === 'slack')
                    }
                    isReadOnly={true}
                    size="lg"
                  />
                  <Box>
                    <Link
                      as={ReactRouterLink}
                      color="kblue.300"
                      to="/settings/integrations"
                    >
                      Connect Slack
                    </Link>{' '}
                    to receive notifications (optional)
                  </Box>
                </Flex>
              </ListItem>
            </List>
            <Button
              colorScheme="kbuttonblue"
              isDisabled={
                !integration.adAccountId || importTask.isTaskInProgress
              }
              isLoading={queueLinkedInImport.isPending}
              onClick={startInitialSync}
              w="fit-content"
            >
              Start Initial Sync
            </Button>
          </Flex>
        </IntegrationSection>
      )}

      <IntegrationSection>
        <Flex direction="column" gap="4">
          <Box fontSize="lg" fontWeight="500">
            Ad Account
          </Box>
          <Box>Select an ad account to use.</Box>
          <Select
            isDisabled={
              updateIntegrationMutation.isPending || importTask.isTaskInProgress
            }
            isLoading={adAccounts.isPending}
            onChange={(newValue) => {
              if (!newValue) {
                return;
              }

              updateIntegrationMutation.mutate({
                type: 'linkedin',
                adAccountId: newValue.value,
              });
            }}
            options={adAccountOptions}
            value={adAccountOptions?.find(
              (option) => option.value === integration.adAccountId
            )}
          />
        </Flex>
      </IntegrationSection>
    </Flex>
  );
};

const InitialImportModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalBody>
          <Flex direction="column" align="center" gap={6}>
            <Image src="/confetti-check.png" alt="Success" mx="auto" />
            <Text fontSize="xl">Congrats! What&apos;s next?</Text>
            <UnorderedList spacing="3">
              <ListItem>
                Keyplay will continuously identify companies clicking your ads.
              </ListItem>
              <ListItem>
                View newly tracked and scored accounts on the{' '}
                <Link
                  as={ReactRouterLink}
                  color="kblue.300"
                  to="/accounts/intent"
                >
                  Intent tab.
                </Link>
              </ListItem>
              <ListItem>
                If enabled, we will send Slack notifications on new account
                engagements.
              </ListItem>
            </UnorderedList>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="kbuttonblue" onClick={onClose} variant="ghost">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
